/**
 * ContactsViewerModal - Modal for viewing original campaign contacts CSV file
 * Uses Glide Data Grid for high-performance rendering of large datasets
 * Supports search with Ctrl+F
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Alert,
  Tooltip,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close,
  Download,
  TableChart,
  Search,
  Analytics,
  ExpandMore,
  ExpandLess,
  InsertDriveFile,
  CheckCircle,
  DeleteOutline,
  CloudDownload,
  Description,
} from '@mui/icons-material';
import DataEditor, { GridCellKind } from '@glideapps/glide-data-grid';
import ImportProgressIndicator, { type ImportProgressData } from './ImportProgressIndicator';
import type { GridColumn, GridCell, Item } from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';
import { useTranslation } from 'react-i18next';
import { lighten } from '@mui/material/styles';
import { useNumberFormat } from '../hooks/useNumberFormat';
import type {
  ContactFilePreviewRequest,
  ContactFilePreviewResponse,
} from '../workers/contactFilePreview.types';

const CONTACTS_VIEWER_PREVIEW_ROW_LIMIT = 200;

function generateColumnName(index: number): string {
  let name = '';
  let currentIndex = index;
  while (currentIndex >= 0) {
    name = String.fromCharCode(97 + (currentIndex % 26)) + name;
    currentIndex = Math.floor(currentIndex / 26) - 1;
  }
  return `column_${name}`;
}

function structurePreviewRows(rawRows: string[][]): { headers: string[]; rows: string[][] } {
  if (rawRows.length === 0) {
    throw new Error('No data found in file');
  }

  let columnCount = 0;
  for (const row of rawRows) {
    if (row.length > columnCount) {
      columnCount = row.length;
    }
  }

  const headers = (rawRows[0] || []).map((header, index) => header.trim() || generateColumnName(index));
  const normalizedHeaders = headers.length > 0
    ? headers
    : Array.from({ length: columnCount }, (_, index) => generateColumnName(index));

  const rows = rawRows.slice(1).map((row) =>
    normalizedHeaders.map((_, index) => row[index] || '')
  );

  if (rows.length === 0) {
    throw new Error('No data rows found in file');
  }

  return { headers: normalizedHeaders, rows };
}

interface ImportStats {
  totalRows?: number;
  total_rows?: number;
  contactsCreated?: number;
  contacts_created?: number;
  phonesCreated?: number;
  phones_created?: number;
  duplicatesRemoved?: number;
  duplicates_removed?: number;
  previouslyConvertedRemoved?: number;
  previously_converted_removed?: number;
  previouslyReachedRemoved?: number;
  previously_reached_removed?: number;
  previouslyOptoutRemoved?: number;
  previously_optout_removed?: number;
  invalidPhonesRemoved?: number;
  invalid_phones_removed?: number;
  wrongTypePhonesRemoved?: number;
  wrong_type_phones_removed?: number;
  contactsWithoutPhones?: number;
  contacts_without_phones?: number;
}

interface ContactsViewerModalProps {
  open: boolean;
  onClose: () => void;
  campaignName: string;
  contactsFileUrl: string | null;
  contactsFileId?: string | null;
  contactsFileOriginalName?: string | null;
  processedContactsFileUrl?: string | null;  // Processed file with status columns
  processedContactsFileId?: string | null;
  // File parsing settings (from campaign)
  fileEncoding?: string;
  fileDelimiter?: string;
  fileTextDelimiter?: string;
  // Import statistics
  importStats?: ImportStats;
  // Import progress (while processing)
  importProgress?: ImportProgressData | null;
}

export default function ContactsViewerModal({
  open,
  onClose,
  campaignName,
  contactsFileUrl,
  contactsFileId,
  contactsFileOriginalName,
  processedContactsFileUrl,
  processedContactsFileId,
  fileEncoding: _fileEncoding,  // Kept for interface compatibility
  fileDelimiter: _fileDelimiter,  // Kept for interface compatibility
  fileTextDelimiter = '"',
  importStats,
  importProgress,
}: ContactsViewerModalProps) {
  const { t } = useTranslation(['pages']);
  const theme = useTheme();
  const { formatNumber, formatPercent } = useNumberFormat();
  const dataGridTheme = useMemo(() => {
    const isLight = theme.palette.mode === 'light';
    const lightHeaderBase = theme.palette.primary.light;
    const lightHeader = lighten(lightHeaderBase, 0.6);
    const lightHeaderHover = lighten(lightHeaderBase, 0.45);
    const lightHeaderFocus = lighten(lightHeaderBase, 0.3);
    const headerBg = isLight ? lightHeader : theme.palette.action.selected;
    const headerHover = isLight ? lightHeaderHover : theme.palette.action.selected;
    const headerFocus = isLight ? lightHeaderFocus : theme.palette.action.selected;
    const headerText = theme.palette.text.primary;

    return {
      bgCell: theme.palette.background.paper,
      bgCellMedium: theme.palette.background.default,
      bgHeader: headerBg,
      bgHeaderHasFocus: headerFocus,
      bgHeaderHovered: headerHover,
      textDark: theme.palette.text.primary,
      textMedium: theme.palette.text.secondary,
      textLight: theme.palette.text.disabled,
      textHeader: headerText,
      textHeaderSelected: headerText,
      accentColor: theme.palette.primary.main,
      accentLight: alpha(theme.palette.primary.main, 0.15),
      borderColor: theme.palette.divider,
      horizontalBorderColor: theme.palette.divider,
      drilldownBorder: theme.palette.primary.main,
      bgSearchResult: alpha(theme.palette.warning.main, 0.25),
      textBubble: theme.palette.text.primary,
    };
  }, [theme]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Download progress state
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [downloadPhase, setDownloadPhase] = useState<'downloading' | 'parsing' | null>(null);
  const [previewTotalRows, setPreviewTotalRows] = useState(0);
  const [previewTruncated, setPreviewTruncated] = useState(false);
  const previewWorkerRef = useRef<Worker | null>(null);
  const previewRequestIdRef = useRef(0);

  // Search state
  const [showSearch, setShowSearch] = useState(false);
  
  // Stats panel state
  const [showStats, setShowStats] = useState(true);
  
  // Download menu state
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const downloadMenuOpen = Boolean(downloadMenuAnchor);
  
  const originalFileExtension = (contactsFileOriginalName?.split('.').pop() || '').toLowerCase();
  const previewFileUrl = processedContactsFileUrl || contactsFileUrl;
  const hasProcessedFile = !!processedContactsFileUrl;
  const hasOriginalFile = !!contactsFileUrl;
  const previewKind: ContactFilePreviewRequest['kind'] = hasProcessedFile || originalFileExtension === 'csv'
    ? 'csv'
    : 'excel';
  const previewFileName = hasProcessedFile
    ? `${campaignName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_preview.csv`
    : (contactsFileOriginalName || 'contacts_preview.csv');
  
  // Processed files always use semicolon delimiter
  const effectiveDelimiter = ';';
  
  // Check if there are any stats to show
  const hasStats = importStats && Object.keys(importStats).length > 0;
  const normalizedImportStats = useMemo(() => {
    const stats = importStats || {};
    return {
      totalRows: stats.totalRows ?? stats.total_rows ?? 0,
      contactsCreated: stats.contactsCreated ?? stats.contacts_created ?? 0,
      phonesCreated: stats.phonesCreated ?? stats.phones_created ?? 0,
      duplicatesRemoved: stats.duplicatesRemoved ?? stats.duplicates_removed ?? 0,
      previouslyConvertedRemoved: stats.previouslyConvertedRemoved ?? stats.previously_converted_removed ?? 0,
      previouslyReachedRemoved: stats.previouslyReachedRemoved ?? stats.previously_reached_removed ?? 0,
      previouslyOptoutRemoved: stats.previouslyOptoutRemoved ?? stats.previously_optout_removed ?? 0,
      invalidPhonesRemoved: stats.invalidPhonesRemoved ?? stats.invalid_phones_removed ?? 0,
      wrongTypePhonesRemoved: stats.wrongTypePhonesRemoved ?? stats.wrong_type_phones_removed ?? 0,
      contactsWithoutPhones: stats.contactsWithoutPhones ?? stats.contacts_without_phones ?? 0,
    };
  }, [importStats]);
  const phonesWithErrors = (normalizedImportStats.invalidPhonesRemoved ?? 0) + (normalizedImportStats.wrongTypePhonesRemoved ?? 0);
  const historicalRemoved = (
    (normalizedImportStats.previouslyConvertedRemoved ?? 0) +
    (normalizedImportStats.previouslyReachedRemoved ?? 0) +
    (normalizedImportStats.previouslyOptoutRemoved ?? 0)
  );
  const hasRemovals = hasStats && (
    (normalizedImportStats.duplicatesRemoved ?? 0) > 0 ||
    historicalRemoved > 0 ||
    phonesWithErrors > 0 ||
    (normalizedImportStats.contactsWithoutPhones ?? 0) > 0
  );

  const effectiveTotalRows = useMemo(() => {
    if (previewTotalRows <= 0) {
      return rows.length;
    }
    return Math.max(previewTotalRows - 1, 0);
  }, [previewTotalRows, rows.length]);

  // Fetch and parse a limited preview of the available contacts file.
  const fetchPreview = useCallback(async () => {
    if (!previewFileUrl) {
      // Don't show error if import is in progress - just wait
      if (!importProgress) {
        setError(t('pages:campaigns.detail.contactsFile.fileNotAvailable'));
      }
      return;
    }

    previewWorkerRef.current?.terminate();
    previewWorkerRef.current = null;
    const requestId = previewRequestIdRef.current + 1;
    previewRequestIdRef.current = requestId;

    setLoading(true);
    setError(null);
    setHeaders([]);
    setRows([]);
    setPreviewTotalRows(0);
    setPreviewTruncated(false);
    setDownloadProgress(0);
    setDownloadPhase('downloading');

    try {
      const response = await fetch(previewFileUrl);
      if (!response.ok) {
        throw new Error(t('pages:campaigns.detail.contactsFile.fetchFailed'));
      }

      // Get content length for progress calculation
      const contentLength = response.headers.get('Content-Length');
      const totalSize = contentLength ? parseInt(contentLength, 10) : 0;
      
      // Use streaming to track download progress
      let fileBlob: Blob;
      if (totalSize > 0 && response.body) {
        const reader = response.body.getReader();
        const chunks: Uint8Array[] = [];
        let receivedLength = 0;
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          chunks.push(value);
          receivedLength += value.length;
          setDownloadProgress(Math.round((receivedLength / totalSize) * 100));
        }
        
        fileBlob = new Blob(
          chunks.map((chunk) => new Uint8Array(chunk)),
          {
            type: response.headers.get('Content-Type') || 'application/octet-stream',
          },
        );
      } else {
        setDownloadProgress(50);
        fileBlob = await response.blob();
      }
      
      setDownloadProgress(100);
      setDownloadPhase('parsing');

      const previewFile = new File([fileBlob], previewFileName, {
        type: fileBlob.type || 'application/octet-stream',
      });

      const worker = new Worker(
        new URL('../workers/contactFilePreview.worker.ts', import.meta.url),
        { type: 'module' },
      );
      previewWorkerRef.current = worker;

      const previewResponse = await new Promise<ContactFilePreviewResponse>((resolve, reject) => {
        worker.onmessage = (event: MessageEvent<ContactFilePreviewResponse>) => {
          resolve(event.data);
        };
        worker.onerror = () => {
          reject(new Error('Error generating file preview'));
        };

        const request: ContactFilePreviewRequest = {
          requestId,
          file: previewFile,
          kind: previewKind,
          encoding: 'utf-8',
          delimiter: effectiveDelimiter,
          quoteChar: fileTextDelimiter,
          previewLimit: CONTACTS_VIEWER_PREVIEW_ROW_LIMIT,
        };

        worker.postMessage(request);
      });

      if (previewResponse.requestId !== previewRequestIdRef.current) {
        return;
      }

      if (!previewResponse.success) {
        throw new Error(previewResponse.error);
      }

      const { headers: previewHeaders, rows: previewRows } = structurePreviewRows(previewResponse.rawRows);
      setHeaders(previewHeaders);
      setRows(previewRows);
      setPreviewTotalRows(previewResponse.totalRows);
      setPreviewTruncated(previewResponse.truncated);
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error?.message || t('pages:campaigns.detail.contactsFile.loadFailed'));
    } finally {
      previewWorkerRef.current?.terminate();
      previewWorkerRef.current = null;
      setLoading(false);
      setDownloadProgress(null);
      setDownloadPhase(null);
    }
  }, [previewFileName, previewFileUrl, previewKind, effectiveDelimiter, fileTextDelimiter, importProgress, t]);

  useEffect(() => {
    if (open) {
      fetchPreview();
    }
  }, [open, fetchPreview]);

  useEffect(() => {
    return () => {
      previewWorkerRef.current?.terminate();
      previewWorkerRef.current = null;
    };
  }, []);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setHeaders([]);
      setRows([]);
      setError(null);
      setShowSearch(false);
      setPreviewTotalRows(0);
      setPreviewTruncated(false);
      setDownloadProgress(null);
      setDownloadPhase(null);
    }
  }, [open]);

  // Build columns from CSV headers
  const columns: GridColumn[] = useMemo(() => {
    return headers.map((header, index) => {
      // System status columns are: _status, _detalhes, or end with _status (e.g., _Celular_1_status)
      // These are added by the processing system, NOT user data columns
      const isSystemStatusCol = header === '_status' || header === '_detalhes' || header.endsWith('_status');
      return {
        title: isSystemStatusCol ? `● ${header}` : header || t('pages:campaigns.detail.contactsFile.column', { index: index + 1 }),
        width: isSystemStatusCol 
          ? Math.max(150, Math.min(250, header.length * 10 + 60))
          : Math.max(100, Math.min(200, header.length * 10 + 40)),
      };
    });
  }, [headers, t]);

  // Get cell content for the grid with status highlighting
  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const rowData = rows[row];
      if (!rowData) {
        return { kind: GridCellKind.Text, data: '', displayData: '', allowOverlay: false };
      }

      const value = rowData[col] || '';
      const header = headers[col] || '';
      // System status columns are: _status, _detalhes, or end with _status
      const isSystemStatusCol = header === '_status' || header === '_detalhes' || header.endsWith('_status');
      
      // Apply color-coded themes for system status columns only
      if (isSystemStatusCol && value) {
        // Determine color based on content
        let themeOverride = {};
        if (value.includes('[OK]') || value.includes('Importado')) {
          themeOverride = { bgCell: 'rgba(76, 175, 80, 0.15)', textDark: '#4caf50' };
        } else if (value.includes('[!]') || value.includes('Parcial')) {
          themeOverride = { bgCell: 'rgba(255, 152, 0, 0.15)', textDark: '#ff9800' };
        } else if (value.includes('[X]') || value.includes('Sem telefone')) {
          themeOverride = { bgCell: 'rgba(244, 67, 54, 0.15)', textDark: '#f44336' };
        } else if (value.includes('[DUP]') || value.includes('Duplicado')) {
          themeOverride = { bgCell: 'rgba(33, 150, 243, 0.15)', textDark: '#2196f3' };
        }
        
        return {
          kind: GridCellKind.Text,
          data: value,
          displayData: value,
          allowOverlay: true,
          themeOverride,
        };
      }

      return {
        kind: GridCellKind.Text,
        data: value,
        displayData: value,
        allowOverlay: true,
      };
    },
    [rows, headers]
  );

  // Download file helper
  const downloadFile = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Download failed:', err);
    }
    setDownloadMenuAnchor(null);
  };

  // Generate slugified campaign name
  const slugifiedCampaignName = campaignName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  
  const handleDownloadProcessed = () => {
    if (processedContactsFileUrl && processedContactsFileId) {
      // Format: {campaign_slug}_{id}_processado.csv
      const filename = `${slugifiedCampaignName}_${processedContactsFileId}_processado.csv`;
      downloadFile(processedContactsFileUrl, filename);
    }
  };
  
  const handleDownloadOriginal = () => {
    if (contactsFileUrl && contactsFileId) {
      // Get original extension from original filename
      const originalExt = contactsFileOriginalName?.split('.').pop() || 'csv';
      // Format: {campaign_slug}_{id}_original.{ext}
      const filename = `${slugifiedCampaignName}_${contactsFileId}_original.${originalExt}`;
      downloadFile(contactsFileUrl, filename);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '80vw',
          height: '80vh',
          maxWidth: '80vw',
          maxHeight: '80vh',
          bgcolor: 'background.paper',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          py: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TableChart color="primary" />
          <Typography variant="h6" component="div">
            {t('pages:campaigns.detail.contactsFile.title', { campaignName })}
          </Typography>
          {rows.length > 0 && (
            <Chip
              label={t('pages:campaigns.detail.contactsFile.rowsColumns', { 
                rows: formatNumber(rows.length),
                columns: headers.length 
              })}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Show badge if viewing processed file */}
          {hasProcessedFile && (
            <Chip
              icon={<CheckCircle />}
              label={t('pages:campaigns.detail.contactsFile.processed')}
              color="success"
              size="small"
              variant="outlined"
            />
          )}
          <Tooltip title={t('pages:campaigns.detail.contactsFile.searchTooltip')}>
            <Button
              startIcon={<Search />}
              onClick={() => setShowSearch(true)}
              disabled={rows.length === 0}
              variant={showSearch ? 'contained' : 'outlined'}
              size="small"
            >
              {t('pages:campaigns.detail.contactsFile.search')}
            </Button>
          </Tooltip>
          <Button
            startIcon={<Download />}
            onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
            disabled={!hasProcessedFile && !hasOriginalFile}
            variant="outlined"
            size="small"
          >
            {t('pages:campaigns.detail.contactsFile.download')}
          </Button>
          <Menu
            anchorEl={downloadMenuAnchor}
            open={downloadMenuOpen}
            onClose={() => setDownloadMenuAnchor(null)}
          >
            {hasProcessedFile && (
              <MenuItem onClick={handleDownloadProcessed}>
                <ListItemIcon>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('pages:campaigns.detail.contactsFile.processedFile')}</ListItemText>
              </MenuItem>
            )}
            {hasOriginalFile && (
              <MenuItem onClick={handleDownloadOriginal}>
                <ListItemIcon>
                  <InsertDriveFile fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('pages:campaigns.detail.contactsFile.originalFile')}</ListItemText>
              </MenuItem>
            )}
          </Menu>
          <IconButton onClick={onClose} edge="end">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Import Progress Indicator (while processing) */}
        {importProgress && (
          <Box sx={{ px: 2, pt: 2 }}>
            <ImportProgressIndicator importProgress={importProgress} />
          </Box>
        )}

        {previewTruncated && !loading && rows.length > 0 && (
          <Alert severity="info" sx={{ mx: 2, mt: 2 }}>
            {t('pages:campaigns.detail.contactsFile.previewLimited', {
              shown: formatNumber(rows.length),
              total: formatNumber(effectiveTotalRows),
            })}
          </Alert>
        )}

        {/* Import Statistics Panel */}
        {hasStats && !importProgress && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Box
              onClick={() => setShowStats(!showStats)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
                cursor: 'pointer',
                bgcolor: hasRemovals ? 'warning.main' : 'info.main',
                color: 'white',
                '&:hover': { opacity: 0.9 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Analytics fontSize="small" />
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('pages:campaigns.detail.contactsFile.importStats')}
                </Typography>
                {hasRemovals && (
                  <Chip
                    label={t('pages:campaigns.detail.contactsFile.recordsRemoved')}
                    size="small"
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      height: 20,
                      fontSize: '0.7rem',
                    }}
                  />
                )}
              </Box>
              {showStats ? <ExpandLess /> : <ExpandMore />}
            </Box>
            <Collapse in={showStats}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: 3, 
                  p: 2,
                  bgcolor: 'background.default',
                }}
              >
                <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('pages:campaigns.detail.contactsFile.totalRows')}
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {formatNumber(normalizedImportStats.totalRows ?? 0)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('pages:campaigns.detail.contactsFile.contactsCreated')}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="success.main">
                    {formatNumber(normalizedImportStats.contactsCreated ?? 0)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {t('pages:campaigns.detail.contactsFile.phonesCreated')}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="success.main">
                    {formatNumber(normalizedImportStats.phonesCreated ?? 0)}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', minWidth: 110 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DeleteOutline sx={{ fontSize: 14 }} />
                    <Typography variant="caption" color="text.secondary">
                      {t('pages:campaigns.detail.contactsFile.duplicates')}
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={700} color="warning.main">
                    {formatNumber(normalizedImportStats.duplicatesRemoved ?? 0)}
                  </Typography>
                </Box>
                {historicalRemoved > 0 && (
                  <Box sx={{ textAlign: 'center', minWidth: 130 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DeleteOutline sx={{ fontSize: 14 }} />
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:campaigns.detail.contactsFile.historical')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={700} color="warning.main">
                      {formatNumber(historicalRemoved)}
                    </Typography>
                  </Box>
                )}
                {(normalizedImportStats.previouslyConvertedRemoved ?? 0) > 0 && (
                  <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DeleteOutline sx={{ fontSize: 14 }} />
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:campaigns.detail.contactsFile.previousConverted')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={700} color="warning.main">
                      {formatNumber(normalizedImportStats.previouslyConvertedRemoved ?? 0)} {t('pages:campaigns.detail.contactsFile.removed')}
                    </Typography>
                  </Box>
                )}
                {(normalizedImportStats.previouslyReachedRemoved ?? 0) > 0 && (
                  <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DeleteOutline sx={{ fontSize: 14 }} />
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:campaigns.detail.contactsFile.previousReached')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={700} color="warning.main">
                      {formatNumber(normalizedImportStats.previouslyReachedRemoved ?? 0)} {t('pages:campaigns.detail.contactsFile.removed')}
                    </Typography>
                  </Box>
                )}
                {(normalizedImportStats.previouslyOptoutRemoved ?? 0) > 0 && (
                  <Box sx={{ textAlign: 'center', minWidth: 120 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DeleteOutline sx={{ fontSize: 14 }} />
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:campaigns.detail.contactsFile.previousOptout')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={700} color="warning.main">
                      {formatNumber(normalizedImportStats.previouslyOptoutRemoved ?? 0)} {t('pages:campaigns.detail.contactsFile.removed')}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ textAlign: 'center', minWidth: 130 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DeleteOutline sx={{ fontSize: 14 }} />
                    <Typography variant="caption" color="text.secondary">
                      {t('pages:campaigns.detail.contactsFile.phonesWithErrors')}
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight={700} color="warning.main">
                    {formatNumber(phonesWithErrors)}
                  </Typography>
                </Box>
                {(normalizedImportStats.wrongTypePhonesRemoved ?? 0) > 0 && (
                  <Box sx={{ textAlign: 'center', minWidth: 100 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DeleteOutline sx={{ fontSize: 14 }} />
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:campaigns.detail.contactsFile.wrongType')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={700} color="warning.main">
                      {formatNumber(normalizedImportStats.wrongTypePhonesRemoved ?? 0)} {t('pages:campaigns.detail.contactsFile.removed')}
                    </Typography>
                  </Box>
                )}
                {(normalizedImportStats.contactsWithoutPhones ?? 0) > 0 && (
                  <Box sx={{ textAlign: 'center', minWidth: 100 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DeleteOutline sx={{ fontSize: 14 }} />
                      <Typography variant="caption" color="text.secondary">
                        {t('pages:campaigns.detail.contactsFile.noPhones')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" fontWeight={700} color="error.main">
                      {formatNumber(normalizedImportStats.contactsWithoutPhones ?? 0)} {t('pages:campaigns.detail.contactsFile.removed')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Error - but not if import is in progress */}
        {error && !importProgress && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading with Progress */}
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, gap: 2, p: 4 }}>
            {downloadProgress !== null ? (
              <Box sx={{ width: '100%', maxWidth: 400 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {downloadPhase === 'downloading' ? <CloudDownload sx={{ fontSize: 18 }} /> : <Description sx={{ fontSize: 18 }} />}
                    <Typography variant="body2" fontWeight={500}>
                      {downloadPhase === 'downloading' ? t('pages:campaigns.detail.contactsFile.downloading') : t('pages:campaigns.detail.contactsFile.parsing')}
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={500} color="primary">
                    {formatPercent(downloadProgress)}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant={downloadPhase === 'parsing' ? 'indeterminate' : 'determinate'}
                  value={downloadProgress}
                  sx={{ height: 8, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                  {downloadPhase === 'downloading' ? t('pages:campaigns.detail.contactsFile.downloadingProgress') : t('pages:campaigns.detail.contactsFile.parsingProgress')}
                </Typography>
              </Box>
            ) : (
              <CircularProgress />
            )}
          </Box>
        )}

        {/* Data Grid */}
        {!loading && rows.length > 0 && (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              scrollbarWidth: 'thin',
              scrollbarColor: `${alpha(theme.palette.text.primary, 0.35)} ${alpha(theme.palette.text.primary, 0.08)}`,
              '& ::-webkit-scrollbar': {
                height: 10,
                width: 10,
              },
              '& ::-webkit-scrollbar-track': {
                backgroundColor: alpha(theme.palette.text.primary, 0.06),
                borderRadius: 999,
              },
              '& ::-webkit-scrollbar-thumb': {
                backgroundColor: alpha(theme.palette.text.primary, 0.3),
                borderRadius: 999,
                border: `2px solid ${alpha(theme.palette.text.primary, 0.06)}`,
              },
              '& ::-webkit-scrollbar-thumb:hover': {
                backgroundColor: alpha(theme.palette.text.primary, 0.45),
              },
            }}
          >
            <DataEditor
              getCellContent={getCellContent}
              columns={columns}
              rows={rows.length}
              smoothScrollX
              smoothScrollY
              rowMarkers="number"
              getCellsForSelection={true}
              width="100%"
              height="100%"
              // Search - simple show/hide, grid handles the rest
              showSearch={showSearch}
              onSearchClose={() => setShowSearch(false)}
              keybindings={{ search: true }}
              theme={dataGridTheme}
            />
          </Box>
        )}

        {/* Empty state */}
        {!loading && rows.length === 0 && !error && !previewFileUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography color="text.secondary">
              {t('pages:campaigns.detail.contactsFile.noFile')}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
